import { Button, Table } from '@/components/ui';
import { ColumnDef } from '@tanstack/react-table';
import { List, Sliders } from '@/components/icons/barrel';
import { useEffect, useMemo, useState } from 'react';
import EditWatchList from '../edit-watch-list';
import useFavoritesStore from '@/store/use-favorites-store';
import Empty from '@/components/ui/empty';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import Symbol from './symbol';
import MidPrice, {
  midPriceAccessorFn,
} from '@/features/markets/components/columns/mid-price';
import PriceChange, {
  priceChangeAccessorFn,
} from '@/features/markets/components/columns/price-change';
import { useNavigate } from 'react-router-dom';

export default function WatchList() {
  const [isWatchListOpen, setIsWatchListOpen] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 items-end justify-between px-4 py-2">
        <Title />
        <EditWatchList open={isWatchListOpen} onOpenChange={setIsWatchListOpen}>
          <Sliders size={14} />
        </EditWatchList>
      </div>

      <WatchListTable onOpen={() => setIsWatchListOpen(true)} />
    </div>
  );
}

function Title() {
  const { t } = useTranslation();
  const length = useFavoritesStore((state) => state.favorites.size);
  return (
    <h4 className="inline-flex items-center gap-2 font-medium leading-none">
      {t('watchList.title')} <span className="font-normal">({length})</span>
    </h4>
  );
}

type TableProps = {
  onOpen(): void;
};

function WatchListTable({ onOpen }: TableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<string>[]>(
    () => [
      {
        accessorFn: (symbol) => symbol,
        header: t('watchList.symbol'),
        cell: ({ row }) => <Symbol symbol={row.original} />,
        meta: {
          width: 'w-[70px] min-w-[70px] max-w-[70px]',
        },
      },
      {
        accessorFn: midPriceAccessorFn,
        header: t('watchList.mid'),
        cell: ({ row }) => <MidPrice symbol={row.original} />,
        meta: {
          width: 'w-[40%] min-w-[90px] text-right',
        },
      },
      {
        accessorFn: priceChangeAccessorFn,
        header: t('watchList.change24h'),
        cell: ({ row }) => <PriceChange symbol={row.original} />,
        meta: {
          width: 'w-[60%] min-w-[110px] text-right',
        },
      },
    ],
    [t],
  );

  const favorites = useFavoritesStore(
    useShallow((state) => Array.from(state.favorites)),
  );

  useEffect(() => {
    const { increaseDependenciesForSymbol, decreaseDependenciesForSymbol } =
      useWebSocketStore.getState();
    favorites.forEach(increaseDependenciesForSymbol);
    return () => {
      favorites.forEach(decreaseDependenciesForSymbol);
    };
  }, [favorites]);

  return (
    <Table
      id="watch-list"
      data={favorites}
      columns={columns}
      className="pb-12 xl:pb-0 [&_td:first-child>div]:ml-0 [&_td:first-child]:pl-4 [&_td:last-child]:pr-4 [&_td>div]:ml-auto [&_th:first-child>div]:justify-start [&_th:first-child]:pl-4 [&_th:last-child]:pr-4 [&_th>div]:flex [&_th>div]:justify-end [&_th]:py-1.5 md:[&_th]:py-2 [&_tr]:text-base"
      isInfiniteScroll
      infiniteScrollOverscan={10}
      emptyComponent={
        <Empty
          title={t('watchList.noItems')}
          description={t('watchList.noItemsDescription')}
        >
          <Button variant="foreground" className="gap-2" onClick={onOpen}>
            <List size={14} strokeWidth={2} /> {t('watchList.manageWatchList')}
          </Button>
        </Empty>
      }
      onRowClick={(v) => navigate(`/trade/${v}`)}
    />
  );
}
